/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";

// react-router-dom components
import { NavLink, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Drag and drop for slides
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import List from "@mui/material/List";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import { Box, Typography, IconButton } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  useMaterialUIController,
} from "context";

function SideSlideNav({
  color,
  university = null,
  brand,
  brandName,
  onMouseLeave,
  onMousePlus,
  onSlideSelect,
  onDeleteSlide,
  onReorderSlides,
  slides = [],
  currentSlideId = null,
  ...rest
}) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const location = useLocation();

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }
  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav
      );
      setWhiteSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : whiteSidenav
      );
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const handleDragEnd = (result) => {
    if (!result.destination || !onReorderSlides) return;

    const reordered = Array.from(slides);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    onReorderSlides(reordered);
  };

  // Use same element colors as dark mode in both themes (white/light borders and text)
  const slideBorder = "1px solid rgba(255,255,255,0.35)";
  const slideBg = "rgba(255,255,255,0.04)";
  const slideHoverBorder = "rgba(255,255,255,0.5)";
  const slideHoverBg = "rgba(255,255,255,0.08)";
  const slideTextColor = "#ffffff";
  const addBlockBorder = "rgba(255,255,255,0.5)";
  const addBlockBg = "rgba(255,255,255,0.02)";
  const addBlockText = "#ffffff";

  const renderSlides = () => (
    <>
      {/* Add Slide Block */}
      <Box
        sx={{
          border: `2px dashed ${addBlockBorder}`,
          borderRadius: "14px",
          height: 112,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          mb: 1.5,
          ml: 2,
          mr: 2,
          transition: "all 0.25s ease",
          bgcolor: addBlockBg,
          "&:hover": {
            borderColor: "rgba(6, 182, 212, 0.5)",
            bgcolor: "rgba(6, 182, 212, 0.08)",
            transform: "scale(1.01)",
          },
        }}
        onClick={() => onMousePlus()}
      >
        <Typography variant="h4" fontWeight={300} sx={{ color: addBlockText }}>
          + New slide
        </Typography>
      </Box>

      {/* Render existing slides with drag-and-drop */}
      <Droppable droppableId="slides-droppable" direction="vertical">
        {(provided) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{ minHeight: 10 }}
          >
            {slides.map((item, index) => {
              const isSelected = currentSlideId && item?.id === currentSlideId;
              return (
                <Draggable
                  key={item?.id ?? index}
                  draggableId={String(item?.id ?? index)}
                  index={index}
                >
                  {(dragProvided) => (
                    <Box
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      sx={{
                        position: "relative",
                        border: isSelected ? "2px solid #06b6d4" : slideBorder,
                        borderRadius: "14px",
                        height: 112,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        mb: 1.5,
                        ml: 2,
                        mr: 2,
                        bgcolor: isSelected ? "rgba(6, 182, 212, 0.12)" : slideBg,
                        boxShadow: isSelected
                          ? "0 0 0 1px rgba(6, 182, 212, 0.3), 0 4px 12px rgba(0,0,0,0.2)"
                          : "0 2px 8px rgba(0,0,0,0.08)",
                        transition: "border-color 0.2s, background-color 0.2s, box-shadow 0.2s, transform 0.2s",
                        "&:hover": {
                          borderColor: isSelected ? "#06b6d4" : slideHoverBorder,
                          bgcolor: isSelected ? "rgba(6, 182, 212, 0.16)" : slideHoverBg,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                          transform: "translateY(-1px)",
                        },
                      }}
                      onClick={() =>
                        onSlideSelect ? onSlideSelect(item) : onMouseLeave()
                      }
                    >
                      {onDeleteSlide && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteSlide(item);
                          }}
                          sx={{
                            position: "absolute",
                            top: 6,
                            left: 6,
                            zIndex: 1,
                            color: "#f87171",
                            bgcolor: "rgba(248, 113, 113, 0.15)",
                            "&:hover": { bgcolor: "rgba(248, 113, 113, 0.28)" },
                            padding: 0.5,
                            transition: "background-color 0.2s",
                          }}
                          title="Delete slide"
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      )}
                      <Typography variant="h5" fontWeight={600} sx={{ color: slideTextColor }}>
                        {index + 1}
                      </Typography>
                    </Box>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </>
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <>
          {university?.logo_url ? (
            <MDBox
              component="img"
              src={university?.logo_url}
              alt="Brand"
              sx={{
                maxHeight: 48,
                maxWidth: "80%",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                display: "block",
                mx: "auto",
              }}
            />
          ) : (
            <MDBox
              component={NavLink}
              to="/"
              display="flex"
              alignItems="center"
            >
              {brand && (
                <MDBox
                  component="img"
                  src={require("../../assets/images/logos/mini_logo.png")}
                  alt="Brand"
                  width="2rem"
                />
              )}
              <MDBox
                width={!brandName && "100%"}
                sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
              >
                <MDTypography
                  component="h6"
                  variant="button"
                  fontWeight="medium"
                  color={textColor}
                >
                  {brandName}
                </MDTypography>
              </MDBox>
            </MDBox>
          )}
        </>
      </MDBox>
      {/* {university?.name && (
        <MDBox px={2} mt={1} mb={0.5} textAlign="center">
          <MDTypography
            variant="caption"
            fontWeight="medium"
            color={textColor}
            sx={{ opacity: 0.8 }}
          >
            {university?.name}
          </MDTypography>
        </MDBox>
      )} */}
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <List>{renderSlides()}</List>
      </DragDropContext>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
SideSlideNav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
SideSlideNav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object),
  onDeleteSlide: PropTypes.func,
  onReorderSlides: PropTypes.func,
};

export default SideSlideNav;
